import { ApplicationRef, Injectable, computed, inject, isDevMode, signal } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { first } from 'rxjs';

/** Cada cuánto se vuelve a preguntar por una versión nueva. */
const INTERVALO_REVISION_MS = 10 * 60 * 1000;

/**
 * Detecta versiones nuevas de la aplicación y las activa.
 *
 * Un service worker sirve la app desde caché, así que sin esto un usuario podría
 * quedarse con una versión vieja indefinidamente: el navegador nunca vuelve a
 * pedir el index. Aquí se busca una versión nueva, se avisa, y solo al aceptar
 * se activa y se recarga.
 */
@Injectable({ providedIn: 'root' })
export class ActualizacionService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly appRef = inject(ApplicationRef);

  private readonly _disponible = signal(false);
  private readonly _activando = signal(false);

  /** Hay una versión nueva descargada y lista para activarse. */
  readonly disponible = this._disponible.asReadonly();
  readonly activando = this._activando.asReadonly();
  readonly habilitado = computed(() => this.swUpdate.isEnabled);

  iniciar(): void {
    if (!this.swUpdate.isEnabled) {
      if (!isDevMode()) {
        console.warn('Service worker deshabilitado: no habrá actualizaciones automáticas.');
      }
      return;
    }

    // Solo VERSION_READY significa "descargada y lista"; VERSION_DETECTED es
    // apenas el aviso de que existe, y activarla en ese punto fallaría.
    this.swUpdate.versionUpdates.subscribe((evento) => {
      switch (evento.type) {
        case 'VERSION_READY':
          this._disponible.set(true);
          break;
        case 'VERSION_INSTALLATION_FAILED':
          console.error(
            `Falló la instalación de la versión ${evento.version.hash}: ${evento.error}`,
          );
          break;
      }
    });

    /**
     * Si la caché quedó corrupta el SW no puede servir la app. Aquí no se le
     * pregunta al usuario: se recarga saltando la caché, porque quedarse es
     * peor que recargar.
     */
    this.swUpdate.unrecoverable.subscribe((evento) => {
      console.error('Estado irrecuperable del service worker:', evento.reason);
      document.location.reload();
    });

    // Esperar a que la app se estabilice: buscar actualizaciones antes compite
    // con el arranque y además `isStable` nunca emite si hay tareas pendientes.
    this.appRef.isStable
      .pipe(first((estable) => estable))
      .subscribe(() => this.revisarPeriodicamente());
  }

  private async revisarPeriodicamente(): Promise<void> {
    try {
      await this.swUpdate.checkForUpdate();
    } catch (error) {
      console.error('Error buscando actualizaciones:', error);
    }
    // Se sigue revisando aunque ya haya una lista: el usuario puede ignorar el
    // aviso y entretanto salir otra versión.
    setTimeout(() => this.revisarPeriodicamente(), INTERVALO_REVISION_MS);
  }

  /** Activa la versión nueva y recarga. */
  async actualizar(): Promise<void> {
    if (this._activando()) return;
    this._activando.set(true);
    try {
      await this.swUpdate.activateUpdate();
      document.location.reload();
    } catch (error) {
      console.error('No se pudo activar la actualización:', error);
      this._activando.set(false);
    }
  }

  descartar(): void {
    this._disponible.set(false);
  }
}
